import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import {
    Autocomplete,
    FormControl,
    FormHelperText,
    FormLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
    useTheme,
    Tooltip,
    Alert,
    Divider,
    CircularProgress,
    Collapse,
} from '@mui/material';
import {
    Close,
    DeleteOutline,
    AutoAwesome,
    Check,
    ContentCopy,
    Refresh,
    ErrorOutline,
    Undo,
} from '@mui/icons-material';
import { font } from '../../styles/font';
import { useEffect, useState } from 'react';
import { useApplicationContext } from '../../context/ApplicationContext';
import useErrorHandler from '../../api/errorHandler/useErrorHandler';
import {
    addProject_API,
    getProjectCategoryList_API,
    getProjectSubcategoryList_API,
} from '../../api/controller/ProjectController';
import { Severity } from '../../constant/ApplicationVariables';
import { useTranslation } from 'react-i18next';
import { AICustomIcon } from '../../components/styled/CustomIcons';
import useAiImproveText from '../hooks/useAiImproveText';

export default function CreateProjectDrawer({ open, handleClose, handleContinue }) {
    const { setProjectList, handleSnackbar, teamsList, setTeamsList } =
        useApplicationContext();
    const theme = useTheme();
    const errorHandler = useErrorHandler();
    const { t } = useTranslation('translation', { keyPrefix: 'project' });

    const [projectData, setProjectData] = useState({
        name: '',
        description: '',
        categoryName: '',
        subCategoryName: '',
    });
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [categoryList, setCategoryList] = useState([]);
    const [subCategoryList, setSubCategoryList] = useState([]);
    const [selectedItemsList, setSelectedItemsList] = useState([]);
    const [inputValue, setInputValue] = useState('');

    // ---------- AI Improvement Hook (suggestion mode) ----------
    const {
        isImproving,
        improvementCount,
        aiError,
        setAiError,
        pendingSuggestion,
        hasPendingSuggestion,
        originalText,
        improve,
        acceptSuggestion,
        revertSuggestion,
        reset: resetAi,
        copyToClipboard,
        totalPrompts,
    } = useAiImproveText({
        onSuggestion: (_, { promptIndex }) => {
            handleSnackbar(
                `AI suggestion ready — accept or revert (${promptIndex + 1}/${totalPrompts})`,
                Severity.info
            );
        },
        onApplied: () => {
            handleSnackbar('Floxpad AI suggestion applied', Severity.success);
        },
        onError: (msg) => handleSnackbar(msg, Severity.error),
    });

    const toggleDrawer = () => {
        handleClose();
        setError(null);
        resetAi();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setProjectData((prev) => ({ ...prev, [name]: value }));

        if (name === 'description') {
            setAiError(null);
        }

        if (error && error[name]) {
            setError((prev) => {
                const newError = { ...prev };
                delete newError[name];
                return Object.keys(newError).length > 0 ? newError : null;
            });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (validateInputs()) handleCreateProject();
    };

    const validateInputs = () => {
        let isValid = true;
        const errorObject = {};

        if (!projectData?.name?.trim()) {
            errorObject.name = 'Name is required.';
            isValid = false;
        } else if (projectData.name.length < 3) {
            errorObject.name = 'Name must be at least 3 characters long.';
            isValid = false;
        } else if (projectData.name.length > 20) {
            errorObject.name = 'Name must be at most 20 characters long.';
            isValid = false;
        }

        if (!projectData?.description?.trim()) {
            errorObject.description = 'Description is required.';
            isValid = false;
        } else if (projectData.description.length < 3) {
            errorObject.description = 'Description must be at least 3 characters long.';
            isValid = false;
        } else if (projectData.description.length > 500) {
            errorObject.description = 'Description must be at most 500 characters long.';
            isValid = false;
        }

        if (!projectData?.categoryName) {
            errorObject.categoryName = 'Category is required.';
            isValid = false;
        }

        if (!projectData?.subCategoryName) {
            errorObject.subCategoryName = 'Sub Category is required.';
            isValid = false;
        }

        setError(Object.keys(errorObject).length > 0 ? errorObject : null);
        return isValid && Object.keys(errorObject).length === 0;
    };

    const handleCreateProject = async () => {
        try {
            setIsLoading(true);

            const body = {
                name: projectData.name.trim(),
                description: projectData.description.trim(),
                categoryName: projectData.categoryName,
                subCategoryName: projectData.subCategoryName,
            };

            const result = await addProject_API(body);

            if (result.success) {
                handleSnackbar(
                    result.message || 'Project created successfully',
                    Severity.success
                );
                handleContinue();
                handleClose();
                setError(null);
                setIsLoading(false);
                resetAi();
                return;
            }

            if (result.fields && Object.keys(result.fields).length > 0) {
                setError(result.fields);
                const firstErrorField = Object.keys(result.fields)[0];
                if (firstErrorField) {
                    const element = document.getElementById(firstErrorField);
                    if (element) {
                        setTimeout(() => {
                            element.focus();
                            element.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                            });
                        }, 100);
                    }
                }
                handleSnackbar('Please fix the highlighted errors', Severity.warning);
                setIsLoading(false);
                return;
            }

            handleSnackbar(
                result.error || 'Failed to create project. Please try again.',
                Severity.error
            );
            setIsLoading(false);
        } catch (error) {
            console.error('Create project error:', error);
            if (error?.fields) {
                setError(error.fields);
                handleSnackbar('Please fix the highlighted errors', Severity.warning);
            } else if (error?.message) {
                handleSnackbar(error.message, Severity.error);
            } else {
                handleSnackbar('An unexpected error occurred', Severity.error);
            }
            setIsLoading(false);
        }
    };

    // ---------- AI Handlers ----------
    const handleImproveText = async () => {
        await improve(projectData.description);
    };

    const handleAcceptSuggestion = () => {
        acceptSuggestion((newText) =>
            setProjectData((prev) => ({ ...prev, description: newText }))
        );
    };

    const handleRevertSuggestion = () => {
        revertSuggestion();
        handleSnackbar('AI suggestion reverted', Severity.info);
    };

    const handleCopySuggestion = async () => {
        if (!pendingSuggestion?.text) return;
        const ok = await copyToClipboard(pendingSuggestion.text);
        handleSnackbar(
            ok ? 'Suggestion copied!' : 'Copy failed',
            ok ? Severity.success : Severity.error
        );
    };

    const handleCopyImproved = async () => {
        const ok = await copyToClipboard(projectData.description);
        handleSnackbar(
            ok ? 'Copied to clipboard!' : 'Copy failed',
            ok ? Severity.success : Severity.error
        );
    };

    const handleResetToOriginal = () => {
        if (originalText) {
            setProjectData((prev) => ({ ...prev, description: originalText }));
            resetAi();
            handleSnackbar('Reset to original description', Severity.info);
        }
    };

    // ---------- Categories ----------
    const handleGetCategoryList = async () => {
        try {
            setIsLoading(true);
            const result = await getProjectCategoryList_API();

            if (result.success) {
                setCategoryList(result.data);
                if (result.data && result.data.length > 0) {
                    setProjectData((prev) => ({
                        ...prev,
                        categoryName: result.data[0].name,
                    }));
                }
            } else {
                errorHandler(result, setIsLoading);
            }
        } catch (err) {
            console.error('Get categories error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleGetSubCategoryList = async () => {
        if (!projectData?.categoryName) return;
        try {
            const query = `parentName=${projectData.categoryName}`;
            const result = await getProjectSubcategoryList_API(query);

            if (result.success) {
                setSubCategoryList(result.data);
                if (result.data && result.data.length > 0) {
                    setProjectData((prev) => ({
                        ...prev,
                        subCategoryName: result.data[0].name,
                    }));
                }
            } else {
                errorHandler(result, setIsLoading);
            }
        } catch (err) {
            console.error('Get subcategories error:', err);
        }
    };

    // ---------- Autocomplete (hidden) ----------
    const handleChangeAutocomplete = (_, newValue) => setSelectedItemsList(newValue);
    const handleInputChange = (_, newInputValue) => setInputValue(newInputValue);
    const handleDeleteItems = (data) =>
        setSelectedItemsList((list) => list.filter((item) => item.email !== data.email));

    useEffect(() => {
        handleGetCategoryList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (projectData?.categoryName) handleGetSubCategoryList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectData?.categoryName]);

    const DrawerList = (
        <Box sx={{ width: 420 }} className="flex flex-col h-full">
            {/* ---------- Header ---------- */}
            <Box className="flex items-center justify-between px-4 py-2 border-b-2">
                <Typography fontSize={font.size.fs_4} color="textPrimary">
                    {t('createProject')}
                </Typography>
                <IconButton onClick={toggleDrawer}>
                    <Close />
                </IconButton>
            </Box>

            <Box component="form" onSubmit={handleSubmit} className="flex-1 flex flex-col">
                <Box className="flex-1 flex flex-col gap-4 px-4 py-4 overflow-y-auto">
                    {/* ---------- Name ---------- */}
                    <FormControl required fullWidth error={!!error?.name}>
                        <FormLabel htmlFor="name">{t('nameText')}</FormLabel>
                        <TextField
                            autoFocus
                            id="name"
                            name="name"
                            placeholder="Enter project name"
                            value={projectData?.name || ''}
                            onChange={handleChange}
                            error={!!error?.name}
                            helperText={error?.name}
                            disabled={isLoading}
                        />
                    </FormControl>

                    {/* ---------- Description + AI ---------- */}
                    <FormControl
                        required
                        fullWidth
                        error={!!error?.description}
                        sx={{ position: 'relative' }}
                    >
                        <FormLabel htmlFor="description">
                            {t('descriptionText')}
                        </FormLabel>

                        <TextField
                            id="description"
                            name="description"
                            placeholder="Enter project description"
                            multiline
                            minRows={4}
                            value={projectData?.description || ''}
                            onChange={handleChange}
                            error={!!error?.description}
                            helperText={error?.description}
                            disabled={isLoading}
                            sx={{
                                '& .MuiInputBase-root': {
                                    paddingBottom: '28px', // leave room for the badge
                                },
                            }}
                            InputProps={{
                                endAdornment: projectData.description?.trim() && (
                                    <InputAdornment
                                        position="end"
                                        sx={{ alignSelf: 'flex-start', mt: 1 }}
                                    >
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            {/* AI icon — hidden while loading */}
                                            <Collapse
                                                in={!isImproving}
                                                orientation="horizontal"
                                                timeout={200}
                                            >
                                                <span>
                                                    <Tooltip
                                                        title={
                                                            hasPendingSuggestion
                                                                ? 'Suggestion pending — accept or revert below'
                                                                : improvementCount > 0
                                                                ? 'Improve text again with Floxpad AI'
                                                                : 'Improve text with Floxpad AI'
                                                        }
                                                        arrow
                                                        placement="top"
                                                    >
                                                        <span>
                                                            <AICustomIcon
                                                                size="xs"
                                                                disabled={
                                                                    isLoading ||
                                                                    hasPendingSuggestion
                                                                }
                                                                onClick={
                                                                    handleImproveText
                                                                }
                                                                showSparks={true}
                                                                showBurst={true}
                                                                showElectricRing={
                                                                    true
                                                                }
                                                                sx={{
                                                                    width: '28px',
                                                                    height: '28px',
                                                                    minWidth: '28px',
                                                                    minHeight:
                                                                        '28px',
                                                                    '& .ai-energy-icon':
                                                                        {
                                                                            width: '16px',
                                                                            height: '16px',
                                                                        },
                                                                }}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                </span>
                                            </Collapse>

                                            {/* Copy */}
                                            <Tooltip
                                                title="Copy text"
                                                arrow
                                                placement="top"
                                            >
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleCopyImproved}
                                                        disabled={isLoading}
                                                        sx={{ p: 0.5 }}
                                                    >
                                                        <ContentCopy fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>

                                            {/* Reset */}
                                            {improvementCount > 0 && originalText && (
                                                <Tooltip
                                                    title="Reset to original"
                                                    arrow
                                                    placement="top"
                                                >
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={
                                                                handleResetToOriginal
                                                            }
                                                            disabled={isLoading}
                                                            sx={{ p: 0.5 }}
                                                        >
                                                            <Refresh fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/*  "Updating..." badge — bottom-right of the textarea */}
                        {isImproving && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: 14,
                                    bottom: error?.description ? 34 : 10,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.6,
                                    px: 1,
                                    py: 0.3,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.background.paper,
                                    border: `1px solid ${theme.palette.primary.main}40`,
                                    boxShadow: `0 2px 8px ${theme.palette.primary.main}25`,
                                    pointerEvents: 'none',
                                    zIndex: 2,
                                    animation: 'aiBadgeFadeIn 250ms ease-out',
                                }}
                            >
                                <CircularProgress
                                    size={10}
                                    thickness={5}
                                    sx={{ color: theme.palette.primary.main }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: '0.68rem',
                                        fontWeight: 600,
                                        color: theme.palette.primary.main,
                                        lineHeight: 1,
                                        letterSpacing: 0.3,
                                    }}
                                >
                                    Updating...
                                </Typography>
                            </Box>
                        )}

                        {/* ---------- AI Suggestion Preview Panel ---------- */}
                        {hasPendingSuggestion && (
                            <Paper
                                elevation={0}
                                sx={{
                                    mt: 1.5,
                                    p: 2,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.primary.main}40`,
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main}0d, ${theme.palette.secondary.main}0d)`,
                                    position: 'relative',
                                    animation:
                                        'aiPanelSlideIn 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                                }}
                            >
                                {/* Header */}
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    mb={1}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={0.75}
                                    >
                                        <AutoAwesome
                                            fontSize="small"
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            fontWeight={700}
                                            sx={{
                                                color: theme.palette.primary.main,
                                                letterSpacing: 0.3,
                                            }}
                                        >
                                            Floxpad AI Suggestion
                                        </Typography>
                                    </Box>

                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={0.5}
                                    >
                                        <Tooltip title="Copy suggestion" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={handleCopySuggestion}
                                                sx={{ p: 0.5 }}
                                            >
                                                <ContentCopy
                                                    sx={{ fontSize: 16 }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Dismiss" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={handleRevertSuggestion}
                                                sx={{ p: 0.5 }}
                                            >
                                                <Close sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                <Divider sx={{ mb: 1.25 }} />

                                {/* Preview text */}
                                <Box
                                    sx={{
                                        maxHeight: 140,
                                        overflowY: 'auto',
                                        pr: 0.5,
                                        mb: 1.5,
                                        '&::-webkit-scrollbar': { width: 4 },
                                        '&::-webkit-scrollbar-thumb': {
                                            borderRadius: 2,
                                            backgroundColor:
                                                theme.palette.divider,
                                        },
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: theme.palette.text.primary,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {pendingSuggestion.text}
                                    </Typography>
                                </Box>

                                {/* Actions */}
                                <Box
                                    display="flex"
                                    gap={1}
                                    justifyContent="flex-end"
                                >
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="inherit"
                                        startIcon={<Undo fontSize="small" />}
                                        onClick={handleRevertSuggestion}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: 2,
                                            borderColor: theme.palette.divider,
                                            color: theme.palette.text.secondary,
                                            '&:hover': {
                                                borderColor:
                                                    theme.palette.text.secondary,
                                                backgroundColor:
                                                    theme.palette.action.hover,
                                            },
                                        }}
                                    >
                                        Revert
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<Check fontSize="small" />}
                                        onClick={handleAcceptSuggestion}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: 2,
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                            color: theme.palette.secondary
                                                .contrastText,
                                            boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
                                            '&:hover': {
                                                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                                                boxShadow: `0 4px 12px ${theme.palette.primary.main}50`,
                                            },
                                        }}
                                    >
                                        Accept Changes
                                    </Button>
                                </Box>
                            </Paper>
                        )}

                        {/* Error */}
                        {aiError && (
                            <Alert
                                severity="error"
                                icon={<ErrorOutline />}
                                sx={{ mt: 1 }}
                                onClose={() => setAiError(null)}
                            >
                                {aiError}
                            </Alert>
                        )}

                        {/* Improvement counter — subtle */}
                        {improvementCount > 0 && !hasPendingSuggestion && (
                            <FormHelperText
                                sx={{
                                    color: theme.palette.success.main,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    fontSize: '0.68rem',
                                    fontWeight: 500,
                                    mt: 0.5,
                                    lineHeight: 1.3,
                                    '& .MuiSvgIcon-root': { fontSize: '0.85rem' },
                                }}
                            >
                                <Check />
                                Floxpad AI Enhanced ({improvementCount} change
                                {improvementCount > 1 ? 's' : ''} applied)
                            </FormHelperText>
                        )}
                    </FormControl>

                    {/* ---------- Category ---------- */}
                    <FormControl required fullWidth error={!!error?.categoryName}>
                        <FormLabel id="category-label">{t('categoryText')}</FormLabel>
                        <Select
                            id="category"
                            name="categoryName"
                            disabled={isLoading || categoryList.length === 0}
                            value={projectData?.categoryName || ''}
                            onChange={handleChange}
                            error={!!error?.categoryName}
                        >
                            {categoryList?.map((item, index) => (
                                <MenuItem value={item.name} key={index}>
                                    {item?.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {error?.categoryName && (
                            <FormHelperText>{error?.categoryName}</FormHelperText>
                        )}
                    </FormControl>

                    {/* ---------- Sub Category ---------- */}
                    <FormControl required fullWidth error={!!error?.subCategoryName}>
                        <FormLabel id="subcategory-label">{t('subCategoryText')}</FormLabel>
                        <Select
                            id="subCategory"
                            name="subCategoryName"
                            disabled={isLoading || subCategoryList.length === 0}
                            value={projectData?.subCategoryName || ''}
                            onChange={handleChange}
                            error={!!error?.subCategoryName}
                        >
                            {subCategoryList?.map((item, index) => (
                                <MenuItem value={item.name} key={index}>
                                    {item?.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {error?.subCategoryName && (
                            <FormHelperText>{error?.subCategoryName}</FormHelperText>
                        )}
                    </FormControl>

                    {/* ---------- Assign Users (Hidden) ---------- */}
                    {false && (
                        <Box className="flex flex-col gap-2">
                            <FormControl fullWidth>
                                <FormLabel htmlFor="users-autocomplete">
                                    {'Assign User'}
                                </FormLabel>
                                <Autocomplete
                                    multiple
                                    id="users-autocomplete"
                                    options={teamsList}
                                    getOptionLabel={(option) =>
                                        `${option.firstName} ${option.lastName} - ${option.email}`
                                    }
                                    value={selectedItemsList}
                                    onChange={handleChangeAutocomplete}
                                    inputValue={inputValue}
                                    onInputChange={handleInputChange}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Search using name / email"
                                            InputProps={{ ...params.InputProps }}
                                        />
                                    )}
                                    renderTags={() => null}
                                    filterSelectedOptions
                                    isOptionEqualToValue={(option, value) =>
                                        option.firstName === value.firstName ||
                                        option.lastName === value.lastName ||
                                        option.email === value.email
                                    }
                                />
                            </FormControl>

                            {selectedItemsList.length > 0 && (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0,
                                        p: 0,
                                        border: `0px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Typography>
                                        Assigned Users ({selectedItemsList.length}):
                                    </Typography>
                                    <Box className="flex flex-col gap-2">
                                        {selectedItemsList.map((project, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    p: 2,
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    borderRadius: 1,
                                                    backgroundColor:
                                                        theme.palette.highlight.main,
                                                }}
                                            >
                                                <Box className="flex-1 flex flex-col">
                                                    <Typography fontWeight="bold">
                                                        {project?.firstName}{' '}
                                                        {project?.lastName}
                                                    </Typography>
                                                    <Typography className="line-clamp-4">
                                                        {project?.email}
                                                    </Typography>
                                                </Box>
                                                <Button
                                                    color="error"
                                                    variant="outlined"
                                                    startIcon={<DeleteOutline />}
                                                    onClick={() =>
                                                        handleDeleteItems(project)
                                                    }
                                                >
                                                    Remove
                                                </Button>
                                            </Box>
                                        ))}
                                    </Box>
                                </Paper>
                            )}
                        </Box>
                    )}
                </Box>

                {/* ---------- Footer ---------- */}
                <Box className="flex items-center gap-4 px-4 py-4 border-t-2 flex-shrink-0">
                    <Button
                        fullWidth
                        variant="outlined"
                        disabled={isLoading}
                        onClick={toggleDrawer}
                    >
                        {t('discardText')}
                    </Button>
                    <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        disabled={isLoading || hasPendingSuggestion}
                        sx={{ color: theme.palette.secondary.contrastText }}
                    >
                        {isLoading ? 'Creating...' : t('createText')}
                    </Button>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Drawer open={open} onClose={handleClose} anchor="right" variant="temporary">
            {DrawerList}
        </Drawer>
    );
}